import { useState, useEffect, useRef } from "react";
import { Menu, X, Tent, Search, Scale, Moon, Sun, BookOpen, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";

interface NavbarProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  vergleichCount?: number;
  isDark: boolean;
  onToggleTheme: () => void;
}

const zeltTypen = [
  { href: "/kuppelzelte", label: "Kuppelzelte" },
  { href: "/tunnelzelte", label: "Tunnelzelte" },
  { href: "/familienzelte", label: "Familienzelte" },
  { href: "/trekkingzelte", label: "Trekkingzelte" },
  { href: "/geodaet-zelte", label: "Geodät-Zelte" },
  { href: "/dachzelte", label: "Dachzelte" },
  { href: "/wurfzelte", label: "Wurfzelte" },
  { href: "/tipi-zelte", label: "Tipi-Zelte" },
  { href: "/winterzelte", label: "Winterzelte" },
  { href: "/festivalzelte", label: "Festivalzelte" },
];

const navLinks = [
  { href: "/", label: "Startseite" },
  { href: "/zelte", label: "Alle Zelte" },
  { href: "/finder", label: "Finder" },
  { href: "#zelttypen", label: "Zelttypen", hasDropdown: true },
  { href: "/ratgeber", label: "Ratgeber" },
  { href: "/vergleich", label: "Vergleich" },
];

interface SearchResult {
  zelte: { name: string; slug: string; marke: string; preis: number }[];
  guides: { titel: string; slug: string; kategorie: string }[];
}

export function Navbar({ currentPath, onNavigate, vergleichCount = 0, isDark, onToggleTheme }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        setSearchOpen(false);
        setSearchQuery("");
        setDropdownOpen(false);
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
        setMobileMenuOpen(false);
        setDropdownOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [currentPath]);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    const timer = setTimeout(() => {
      fetch(`/api/suche?q=${encodeURIComponent(searchQuery.trim())}`)
        .then(res => res.json())
        .then(data => setSearchResults({ zelte: data.zelte || data.data || [], guides: data.guides || [] }))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!searchOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setSearchQuery("");
        setSearchResults(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchOpen]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onNavigate(`/zelte?suche=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery("");
      setSearchResults(null);
    }
  };

  const handleResultClick = (path: string) => {
    onNavigate(path);
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults(null);
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-500",
        scrolled
          ? "glass border-b shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.3)]"
          : "bg-transparent border-b border-transparent"
      )}
    >
      <Container>
        <nav aria-label="Hauptnavigation" className="flex h-16 md:h-18 items-center justify-between">
          {/* Logo */}
          <a
            href="/"
            onClick={e => {
              e.preventDefault();
              onNavigate("/");
            }}
            className="group flex items-center gap-2.5 font-bold text-xl"
          >
            <div className="relative">
              <div className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300",
                "bg-gradient-to-br from-primary to-primary/80",
                "group-hover:shadow-lg group-hover:shadow-primary/25 group-hover:scale-105"
              )}>
                <Tent className="h-5 w-5 text-primary-foreground" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-background animate-pulse" />
            </div>
            <div className="flex flex-col">
              <span className="text-foreground leading-none font-bold tracking-tight">zelt.kaufen</span>
              <span className="text-[10px] text-muted-foreground font-medium tracking-wide">VERGLEICHSPORTAL</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              link.hasDropdown ? (
                <div key={link.href} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    aria-expanded={dropdownOpen}
                    aria-haspopup="true"
                    className={cn(
                      "relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-1",
                      dropdownOpen
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    {dropdownOpen && (
                      <span className="absolute inset-0 bg-primary/10 rounded-lg" />
                    )}
                    <span className="relative flex items-center gap-1">
                      {link.label}
                      <ChevronDown className={cn(
                        "w-3.5 h-3.5 transition-transform duration-200",
                        dropdownOpen && "rotate-180"
                      )} />
                    </span>
                  </button>

                  {/* Dropdown */}
                  <div className={cn(
                    "absolute left-0 top-full mt-2 z-50 w-56",
                    "bg-background border rounded-xl shadow-lg overflow-hidden",
                    "origin-top transition-all duration-200 ease-out",
                    dropdownOpen
                      ? "opacity-100 scale-100 translate-y-0"
                      : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                  )}>
                    <div className="p-1.5">
                      {zeltTypen.map(item => (
                        <a
                          key={item.href}
                          href={item.href}
                          onClick={e => {
                            e.preventDefault();
                            onNavigate(item.href);
                            setDropdownOpen(false);
                          }}
                          className={cn(
                            "block px-3 py-2 rounded-lg text-sm transition-colors",
                            currentPath === item.href
                              ? "bg-primary/10 text-primary font-medium"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          {item.label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={e => {
                    e.preventDefault();
                    onNavigate(link.href);
                  }}
                  className={cn(
                    "relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300",
                    currentPath === link.href
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  {currentPath === link.href && (
                    <span className="absolute inset-0 bg-primary/10 rounded-lg" />
                  )}
                  <span className="relative">
                    {link.label}
                    {link.href === "/vergleich" && vergleichCount > 0 && (
                      <span className="ml-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[10px] font-bold bg-primary text-primary-foreground rounded-full">
                        {vergleichCount}
                      </span>
                    )}
                  </span>
                </a>
              )
            ))}
          </div>

          {/* Search & Actions */}
          <div className="flex items-center gap-1.5">
            {searchOpen ? (
              <div ref={searchContainerRef} className="relative flex items-center animate-in fade-in slide-in-from-right-4 duration-300">
                <form onSubmit={handleSearch} role="search" aria-label="Zelte und Ratgeber suchen" className="flex items-center">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      aria-label="Zelte und Ratgeber suchen"
                      placeholder="Suchen... ⌘K"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className={cn(
                        "w-44 md:w-72 h-10 pl-10 pr-4 rounded-xl",
                        "bg-muted/50 border border-border/50",
                        "text-sm placeholder:text-muted-foreground",
                        "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50",
                        "transition-all duration-300"
                      )}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="ml-1 h-10 w-10 rounded-xl hover:bg-muted/50"
                    onClick={() => { setSearchOpen(false); setSearchQuery(""); setSearchResults(null); }}
                    aria-label="Suche schliessen"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </form>

                {searchResults && (searchResults.zelte.length > 0 || searchResults.guides.length > 0) && (
                  <div className="absolute top-full right-0 mt-2 w-72 md:w-96 bg-background border rounded-xl shadow-xl overflow-hidden z-50 max-h-80 overflow-y-auto">
                    {searchResults.zelte.length > 0 && (
                      <div>
                        <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30 flex items-center gap-1.5">
                          <Tent className="w-3 h-3" />
                          Zelte ({searchResults.zelte.length})
                        </div>
                        {searchResults.zelte.slice(0, 5).map(z => (
                          <button
                            key={z.slug}
                            onClick={() => handleResultClick(`/zelte/${z.slug}`)}
                            className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-center justify-between gap-2"
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{z.name}</div>
                              <div className="text-xs text-muted-foreground">{z.marke}</div>
                            </div>
                            <span className="text-sm font-semibold text-primary flex-shrink-0">{z.preis}&euro;</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchResults.guides.length > 0 && (
                      <div>
                        <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30 flex items-center gap-1.5">
                          <BookOpen className="w-3 h-3" />
                          Ratgeber ({searchResults.guides.length})
                        </div>
                        {searchResults.guides.slice(0, 3).map(g => (
                          <button
                            key={g.slug}
                            onClick={() => handleResultClick(`/ratgeber/${g.slug}`)}
                            className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors"
                          >
                            <div className="text-sm font-medium truncate">{g.titel}</div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchQuery.trim().length >= 2 && (
                      <button
                        onClick={() => handleSearch({ preventDefault: () => {} } as React.FormEvent)}
                        className="w-full text-center px-3 py-2.5 text-sm text-primary hover:bg-muted/50 transition-colors border-t"
                      >
                        Alle Ergebnisse anzeigen &rarr;
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchOpen(true)}
                  className="h-10 w-10 rounded-xl hover:bg-muted/50 transition-all duration-300"
                  aria-label="Suche oeffnen"
                >
                  <Search className="h-5 w-5" />
                  <span className="sr-only">Suche</span>
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-10 w-10 rounded-xl hover:bg-muted/50 transition-all duration-300"
                  onClick={() => onNavigate("/vergleich")}
                  aria-label={vergleichCount > 0 ? `Vergleich mit ${vergleichCount} Zelten oeffnen` : "Vergleich oeffnen"}
                >
                  <Scale className="h-5 w-5" />
                  {vergleichCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full flex items-center justify-center animate-in zoom-in duration-300">
                      {vergleichCount}
                    </span>
                  )}
                  <span className="sr-only">Vergleich</span>
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onToggleTheme}
                  className="h-10 w-10 rounded-xl hover:bg-muted/50 transition-all duration-300"
                  aria-label={isDark ? "Helles Design aktivieren" : "Dunkles Design aktivieren"}
                >
                  <div className="relative w-5 h-5">
                    <Sun className={cn(
                      "absolute inset-0 h-5 w-5 transition-all duration-500",
                      isDark ? "rotate-0 scale-100 opacity-100" : "rotate-90 scale-0 opacity-0"
                    )} />
                    <Moon className={cn(
                      "absolute inset-0 h-5 w-5 transition-all duration-500",
                      isDark ? "-rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100"
                    )} />
                  </div>
                  <span className="sr-only">Theme wechseln</span>
                </Button>
              </>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-10 w-10 rounded-xl hover:bg-muted/50 transition-all duration-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? "Menue schliessen" : "Menue oeffnen"}
            >
              <div className="relative w-5 h-5">
                <Menu className={cn(
                  "absolute inset-0 h-5 w-5 transition-all duration-300",
                  mobileMenuOpen ? "rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100"
                )} />
                <X className={cn(
                  "absolute inset-0 h-5 w-5 transition-all duration-300",
                  mobileMenuOpen ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-0 opacity-0"
                )} />
              </div>
              <span className="sr-only">Menü</span>
            </Button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <div className={cn(
          "md:hidden overflow-hidden transition-all duration-500 ease-out",
          mobileMenuOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        )}>
          <div className="py-4 space-y-1">
            {[
              { href: "/", label: "Startseite" },
              { href: "/zelte", label: "Alle Zelte" },
              { href: "/finder", label: "Zelt-Finder" },
              { href: "/ratgeber", label: "Ratgeber" },
              { href: "/vergleich", label: "Vergleich" },
            ].map((link, index) => (
              <a
                key={link.href}
                href={link.href}
                onClick={e => {
                  e.preventDefault();
                  onNavigate(link.href);
                  setMobileMenuOpen(false);
                }}
                className={cn(
                  "flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300",
                  mobileMenuOpen && "animate-in slide-in-from-left-4 fade-in",
                  currentPath === link.href
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                )}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {link.label}
                {link.href === "/vergleich" && vergleichCount > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[10px] font-bold bg-primary text-primary-foreground rounded-full">
                    {vergleichCount}
                  </span>
                )}
              </a>
            ))}

            {/* Mobile Zelttypen */}
            <div className="pt-2 pb-1 px-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Zelttypen</p>
            </div>
            <div className="grid grid-cols-2 gap-1 px-2">
              {zeltTypen.map((item, index) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={e => {
                    e.preventDefault();
                    onNavigate(item.href);
                    setMobileMenuOpen(false);
                  }}
                  className={cn(
                    "px-3 py-2 text-sm rounded-lg transition-colors",
                    mobileMenuOpen && "animate-in slide-in-from-left-4 fade-in",
                    currentPath === item.href
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  )}
                  style={{ animationDelay: `${(index + 4) * 30}ms` }}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </header>
  );
}
